/**
 * @file Layer0 Reveal Map — сцена после первого клика
 * @module membrane/src/layers/RevealMapScene
 *
 * Лёгкая 2D-композиция: фон + пыль + стеклянные сферы + тонкие филаменты.
 * Без тяжёлого WebGL, с адаптивным FPS и ограничением DPR.
 */

import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, WheelEvent as ReactWheelEvent } from 'react';
import { BaseLayerProps } from '../types/membrane';
import { getMap, getSeed } from '../services/mirrorApi';

type QualityPreset = 'low' | 'medium' | 'high';
type BackgroundVariant = 'reference' | 'clean';
type SphereVariant = 'procedural' | 'sprite-full' | 'sprite-ring';

interface RevealMapProps extends BaseLayerProps {
  clickPosition?: { x: number; y: number };
  onEnterFlow?: () => void;
}

interface SphereNode {
  id: string;
  x: number; // 0..1
  y: number; // 0..1
  size: number; // px
  weight?: number;
}

interface LinkEdge {
  from: string;
  to: string;
  weight?: number;
}

interface MirrorMap {
  nodes: SphereNode[];
  edges: LinkEdge[];
}

const PRESETS: Record<
  QualityPreset,
  {
    dprCap: number;
    renderScale: number;
    dustCount: number;
    sphereLimit: number;
    baseFilamentAlpha: number;
    hoverFilamentAlpha: number;
    hoverLinks: number;
    fpsIdle: number;
    fpsActive: number;
  }
> = {
  low: {
    dprCap: 1.0,
    renderScale: 0.6,
    dustCount: 140,
    sphereLimit: 16,
    baseFilamentAlpha: 0.93,
    hoverFilamentAlpha: 0.8,
    hoverLinks: 3,
    fpsIdle: 18,
    fpsActive: 40,
  },
  medium: {
    dprCap: 1.0,
    renderScale: 0.75,
    dustCount: 240,
    sphereLimit: 26,
    baseFilamentAlpha: 0.5,
    hoverFilamentAlpha: 0.922,
    hoverLinks: 5,
    fpsIdle: 24,
    fpsActive: 60,
  },
  high: {
    dprCap: 1.25,
    renderScale: 0.9,
    dustCount: 420,
    sphereLimit: 38,
    baseFilamentAlpha: 0.7,
    hoverFilamentAlpha: 0.96,
    hoverLinks: 16,
    fpsIdle: 30,
    fpsActive: 60,
  },
};

const normalizeCoord = (value: number) => (value > 1 ? value / 100 : value);

const seedRandom = (seed: string) => {
  let s = 0;
  for (let i = 0; i < seed.length; i += 1) {
    s = (s * 31 + seed.charCodeAt(i)) % 2147483647;
  }
  return () => {
    s = (s * 48271) % 2147483647;
    return s / 2147483647;
  };
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const hashFloat = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return ((hash >>> 0) % 10000) / 10000;
};

const createSphereTexture = () => {
  const texture = document.createElement('canvas');
  texture.width = 256;
  texture.height = 256;
  const ctx = texture.getContext('2d');
  if (!ctx) return texture;

  const center = 128;
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 8; i += 1) {
    const r = 24 + i * 12;
    ctx.beginPath();
    ctx.arc(center, center, r, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  for (let i = 0; i < 10; i += 1) {
    const angle = (i / 10) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(center, center);
    ctx.lineTo(center + Math.cos(angle) * 110, center + Math.sin(angle) * 110);
    ctx.stroke();
  }

  return texture;
};

const normalizeLayout = (nodes: SphereNode[]) => {
  if (nodes.length === 0) return nodes;
  let minX = 1;
  let maxX = 0;
  let minY = 1;
  let maxY = 0;
  nodes.forEach((node) => {
    minX = Math.min(minX, node.x);
    maxX = Math.max(maxX, node.x);
    minY = Math.min(minY, node.y);
    maxY = Math.max(maxY, node.y);
  });
  const spanX = Math.max(0.0001, maxX - minX);
  const spanY = Math.max(0.0001, maxY - minY);
  const margin = 0.08;
  const scaleX = (1 - margin * 2) / spanX;
  const scaleY = (1 - margin * 2) / spanY;
  const scale = Math.min(scaleX, scaleY);

  return nodes.map((node) => ({
    ...node,
    x: Math.min(1 - margin, Math.max(margin, (node.x - minX) * scale + margin)),
    y: Math.min(1 - margin, Math.max(margin, (node.y - minY) * scale + margin)),
  }));
};

const relaxLayout = (nodes: SphereNode[], iterations = 18) => {
  const result = nodes.map((node) => ({ ...node }));
  for (let iter = 0; iter < iterations; iter += 1) {
    for (let i = 0; i < result.length; i += 1) {
      const a = result[i];
      let vx = 0;
      let vy = 0;
      for (let j = 0; j < result.length; j += 1) {
        if (i === j) continue;
        const b = result[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist = Math.hypot(dx, dy) || 0.0001;
        const minDist = (a.size + b.size) / 1200 + 0.02;
        if (dist < minDist) {
          const force = (minDist - dist) * 0.5;
          vx += (dx / dist) * force;
          vy += (dy / dist) * force;
        }
      }
      a.x = Math.min(0.98, Math.max(0.02, a.x + vx));
      a.y = Math.min(0.98, Math.max(0.02, a.y + vy));
    }
  }
  return result;
};

/**
 * RevealMapScene — новая лёгкая сцена кристаллизации
 */
export default function RevealMapScene({
  onProceed,
  onReturn: _onReturn,
  clickPosition,
}: RevealMapProps) {
  const [quality, setQuality] = useState<QualityPreset>('medium');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seed, setSeed] = useState<string>('seed');
  const [mapData, setMapData] = useState<MirrorMap>({ nodes: [], edges: [] });
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [revealTimes, setRevealTimes] = useState<Map<string, number>>(new Map());
  const [metrics, setMetrics] = useState<{ fps: number; frameMs: number; draws: number } | null>(null);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [staticBg, setStaticBg] = useState(true);
  const [filamentFade, setFilamentFade] = useState(0);
  const [bgVariant, setBgVariant] = useState<BackgroundVariant>('clean');
  const [sphereVariant, setSphereVariant] = useState<SphereVariant>('procedural');
  const [isDragging, setIsDragging] = useState(false);
  const [enteringSphere, setEnteringSphere] = useState<{
    id: string;
    x: number;
    y: number;
    radius: number;
    scale: number;
  } | null>(null);
  const [linkParams, setLinkParams] = useState({
    idleAlpha: 0.3,
    hoverAlpha: 0.71,
    holdSeconds: 19,
    fadeSeconds: 7,
    curveAmp: 26,
    driftAmp: 6,
    edgeStepIdle: 3,
    assembleEnabled: true,
    assembleSeconds: 0.6,
    fogBaseScale: 3.0,
    fogCurvature: 0.7,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textureRef = useRef<HTMLCanvasElement | null>(null);
  const spriteFullRef = useRef<HTMLImageElement | null>(null);
  const spriteRingRef = useRef<HTMLImageElement | null>(null);
  const linkActivityRef = useRef<Map<string, number>>(new Map());
  const linkAssembleRef = useRef<Map<string, number>>(new Map());
  const enterTimerRef = useRef<number | null>(null);
  const hiddenSphereIdRef = useRef<string | null>(null);
  const dragRef = useRef<{
    id: string;
    startX: number;
    startY: number;
  } | null>(null);
  const lastInteractionRef = useRef<number>(0);
  const isInteractingRef = useRef<boolean>(false);
  const revealStartRef = useRef<number>(0);
  const mouseRef = useRef<{ x: number; y: number }>({ x: 0.5, y: 0.5 });
  const animationRef = useRef<number | null>(null);
  const lastRenderTimeRef = useRef<number>(0);
  const frameCountRef = useRef<number>(0);
  const lastFpsTimeRef = useRef<number>(0);
  const isPausedRef = useRef<boolean>(false);

  const preset = PRESETS[quality];
  const focus = useMemo(() => clickPosition || { x: 50, y: 50 }, [clickPosition]);

  const backgroundImage = useMemo(() => {
    const base = bgVariant === 'clean' ? '/reveal_bg_clean.png' : '/multy_sfere.png';
    return `url('${base}'), url('/reveal_bg.svg')`;
  }, [bgVariant]);

  const dustPoints = useMemo(() => {
    const rng = seedRandom(seed);
    return Array.from({ length: preset.dustCount }).map(() => ({
      x: rng(),
      y: rng(),
      radius: 0.6 + rng() * 1.6,
      alpha: 0.15 + rng() * 0.4,
      twinkle: rng() * Math.PI * 2,
    }));
  }, [seed, preset.dustCount]);

  const spheres = useMemo(() => {
    const nodes = [...mapData.nodes];
    nodes.sort((a, b) => (b.weight ?? b.size) - (a.weight ?? a.size));
    const limit = Math.min(nodes.length, preset.sphereLimit);
    const normalized = nodes.slice(0, limit).map((node) => {
      const edgeFactor = Math.max(Math.abs(node.x - 0.5), Math.abs(node.y - 0.5));
      const scale = (0.85 + edgeFactor * 0.7) * 1.25;
      return {
        ...node,
        size: Math.max(18, node.size * scale),
      };
    });
    return normalized.map((node) => ({
      ...node,
      x: Math.min(0.98, Math.max(0.02, node.x)),
      y: Math.min(0.98, Math.max(0.02, node.y)),
    }));
  }, [mapData.nodes, preset.sphereLimit]);

  const visibleSpheres = useMemo(
    () => spheres.filter((node) => revealedIds.has(node.id)),
    [spheres, revealedIds]
  );

  const sphereOffsets = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>();
    spheres.forEach((node) => {
      const key = `${node.id}-${seed}`;
      map.set(node.id, {
        x: (hashFloat(`${key}-ox`) - 0.5) * 0.2,
        y: (hashFloat(`${key}-oy`) - 0.5) * 0.2,
      });
    });
    return map;
  }, [spheres, seed]);

  const coreSpheres = useMemo(() => spheres.slice(0, 4), [spheres]);

  const lightCoreGradients = useMemo(() => {
    return coreSpheres
      .map((node) => {
        const x = node.x * 100;
        const y = node.y * 100;
        return `radial-gradient(circle at ${x}% ${y}%, rgba(255,240,220,0.35), rgba(255,240,220,0.0) 55%)`;
      })
      .join(', ');
  }, [coreSpheres]);

  const linkSliderItems: Array<{
    key: keyof typeof linkParams;
    label: string;
    min: number;
    max: number;
    step: number;
    isInt?: boolean;
  }> = [
    { key: 'idleAlpha', label: 'Idle альфа', min: 0.0, max: 0.3, step: 0.01 },
    { key: 'hoverAlpha', label: 'Hover альфа', min: 0.05, max: 0.9, step: 0.01 },
    { key: 'assembleSeconds', label: 'Скорость сборки, c', min: 0.1, max: 2.0, step: 0.05 },
          { key: 'fogBaseScale', label: 'Стартовая ширина', min: 0.8, max: 4.2, step: 0.05 },
          { key: 'fogCurvature', label: 'Отдаление изгиба', min: 0.1, max: 2.5, step: 0.1 },
    { key: 'holdSeconds', label: 'Удержание, c', min: 5, max: 30, step: 1, isInt: true },
    { key: 'fadeSeconds', label: 'Затухание, c', min: 2, max: 10, step: 1, isInt: true },
    { key: 'curveAmp', label: 'Кривизна', min: 6, max: 36, step: 1, isInt: true },
    { key: 'driftAmp', label: 'Дрейф', min: 0, max: 12, step: 1, isInt: true },
    { key: 'edgeStepIdle', label: 'Редкость (idle)', min: 1, max: 5, step: 1, isInt: true },
  ];

  const edges = useMemo(() => {
    const ids = new Set(spheres.map((node) => node.id));
    return mapData.edges.filter((edge) => ids.has(edge.from) && ids.has(edge.to));
  }, [mapData.edges, spheres]);

  const adjacency = useMemo(() => {
    const map = new Map<string, string[]>();
    edges.forEach((edge) => {
      const list = map.get(edge.from) || [];
      list.push(edge.to);
      map.set(edge.from, list);
    });
    return map;
  }, [edges]);

  const edgeControlMap = useMemo(() => {
    const map = new Map<string, { tx: number; ty: number; amp: number }>();
    edges.forEach((edge) => {
      const seedKey = `${edge.from}-${edge.to}-${seed}`;
      map.set(seedKey, {
        tx: hashFloat(seedKey),
        ty: hashFloat(`${seedKey}-y`),
        amp: 16 + hashFloat(`${seedKey}-a`) * 18,
      });
    });
    return map;
  }, [edges, seed]);

  useEffect(() => {
    const img = new Image();
    img.src = '/sphere_grid.svg';
    img.onload = () => {
      const texture = document.createElement('canvas');
      texture.width = 256;
      texture.height = 256;
      const ctx = texture.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, texture.width, texture.height);
      textureRef.current = texture;
    };
    img.onerror = () => {
      textureRef.current = createSphereTexture();
    };
  }, []);

  useEffect(() => {
    const full = new Image();
    full.src = '/sphere_sprite_full.png';
    full.onload = () => {
      spriteFullRef.current = full;
    };
    const ring = new Image();
    ring.src = '/sphere_sprite_ring.png';
    ring.onload = () => {
      spriteRingRef.current = ring;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const seedData = await getSeed();
        if (!mounted) return;
        setSeed(seedData.seed);
        const map = await getMap(seedData.seed, focus.x, focus.y);
        if (!mounted) return;
        const normalizedNodes: SphereNode[] = map.nodes.map((node) => {
          const x = normalizeCoord(node.x);
          const y = normalizeCoord(node.y);
          const base = node.size && node.size > 1 ? node.size : 40;
          const sizePx = Math.max(24, base * 0.6);
          return {
            id: String(node.id),
            x,
            y,
            size: sizePx,
            weight: node.size,
          };
        });
        const distributedNodes = relaxLayout(normalizeLayout(normalizedNodes));
        const normalizedEdges: LinkEdge[] = map.edges.map((edge) => ({
          from: String(edge.from),
          to: String(edge.to),
          weight: edge.type ? 1 : 0.5,
        }));
        setMapData({ nodes: distributedNodes, edges: normalizedEdges });
        // Изначально показываем 7 сфер в зоне клика
        const focusX = normalizeCoord(focus.x);
        const focusY = normalizeCoord(focus.y);
        const initial = [...distributedNodes]
          .map((node) => ({
            id: node.id,
            dist: Math.hypot(node.x - focusX, node.y - focusY),
          }))
          .sort((a, b) => a.dist - b.dist)
          .slice(0, 7)
          .map((entry) => entry.id);
        setRevealedIds(new Set(initial));
        setRevealTimes(() => {
          const next = new Map<string, number>();
          const now = performance.now();
          initial.forEach((id) => next.set(id, now));
          return next;
        });
      } catch (err) {
        console.error('RevealMap load error:', err);
        setError('Не удалось загрузить карту');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [focus.x, focus.y]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, preset.dprCap);
      canvas.width = rect.width * dpr * preset.renderScale;
      canvas.height = rect.height * dpr * preset.renderScale;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [preset.dprCap, preset.renderScale]);

  useEffect(() => {
    const markInteraction = () => {
      lastInteractionRef.current = Date.now();
    };
    const handleVisibility = () => {
      isPausedRef.current = document.hidden;
    };
    window.addEventListener('mousemove', markInteraction);
    window.addEventListener('wheel', markInteraction, { passive: true });
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('mousemove', markInteraction);
      window.removeEventListener('wheel', markInteraction as EventListener);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);
 
  useEffect(() => {
    return () => {
      if (enterTimerRef.current) {
        window.clearTimeout(enterTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      isInteractingRef.current = hoveredId !== null || Date.now() - lastInteractionRef.current < 1200;
    }, 300);
    return () => clearInterval(interval);
  }, [hoveredId]);

  useEffect(() => {
    if (!hoveredId) return;
    const neighbors = adjacency.get(hoveredId) || [];
    if (neighbors.length === 0) return;
    setRevealedIds((prev) => {
      const next = new Set(prev);
      next.add(hoveredId);
      neighbors.slice(0, preset.hoverLinks).forEach((id) => next.add(id));
      return next;
    });
    setRevealTimes((prev) => {
      const next = new Map(prev);
      const now = performance.now();
      next.set(hoveredId, next.get(hoveredId) ?? now);
      neighbors.slice(0, preset.hoverLinks).forEach((id) => {
        if (!next.has(id)) next.set(id, now);
      });
      return next;
    });
  }, [hoveredId, adjacency, preset.hoverLinks]);

  useEffect(() => {
    const fadeTimer = setInterval(() => {
      setFilamentFade((prev) => {
        const target = hoveredId ? 1 : 0;
        const next = prev + (target - prev) * 0.08;
        return Math.abs(next - target) < 0.01 ? target : next;
      });
    }, 60);
    return () => clearInterval(fadeTimer);
  }, [hoveredId]);

  useEffect(() => {
    revealStartRef.current = performance.now();
  }, [focus.x, focus.y]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const render = (time: number) => {
      if (isPausedRef.current) {
        animationRef.current = requestAnimationFrame(render);
        return;
      }

      const fpsCap = isInteractingRef.current ? preset.fpsActive : preset.fpsIdle;
      const minDelta = 1000 / fpsCap;
      if (time - lastRenderTimeRef.current < minDelta) {
        animationRef.current = requestAnimationFrame(render);
        return;
      }
      const frameMs = lastRenderTimeRef.current > 0 ? time - lastRenderTimeRef.current : minDelta;
      lastRenderTimeRef.current = time;

      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      const mx = mouseRef.current.x - 0.5;
      const my = mouseRef.current.y - 0.5;

      const dustOffset = { x: mx * 35, y: my * 35 };
      const sphereOffset = { x: mx * 12, y: my * 12 };
      const zoomScale = zoom;

      // Dust layer
      ctx.save();
      ctx.translate(dustOffset.x, dustOffset.y);
      ctx.translate(width * 0.5, height * 0.5);
      ctx.scale(zoomScale, zoomScale);
      ctx.translate(-width * 0.5, -height * 0.5);
      ctx.globalCompositeOperation = 'lighter';
      dustPoints.forEach((point, index) => {
        const twinkle = 0.2 + Math.sin(time * 0.0006 + point.twinkle) * 0.15;
        ctx.globalAlpha = point.alpha + twinkle * 0.1;
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.beginPath();
        ctx.arc(point.x * width, point.y * height, point.radius, 0, Math.PI * 2);
        ctx.fill();

        // Редкие "крестики" для звездной пыли
        if (index % 23 === 0) {
          const size = point.radius * 5;
          ctx.globalAlpha = point.alpha * 0.6;
          ctx.strokeStyle = 'rgba(255,255,255,0.6)';
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(point.x * width - size, point.y * height);
          ctx.lineTo(point.x * width + size, point.y * height);
          ctx.moveTo(point.x * width, point.y * height - size);
          ctx.lineTo(point.x * width, point.y * height + size);
          ctx.stroke();
        }
      });
      ctx.restore();

      // Core light sources (soft glow)
      ctx.save();
      ctx.translate(sphereOffset.x, sphereOffset.y);
      ctx.translate(width * 0.5, height * 0.5);
      ctx.scale(zoomScale, zoomScale);
      ctx.translate(-width * 0.5, -height * 0.5);
      coreSpheres.forEach((node, index) => {
        // Скрываем световой источник, если сфера является целью анимации приближения
        const nodeIdStr = String(node.id);
        if ((enteringSphere && String(enteringSphere.id) === nodeIdStr) || hiddenSphereIdRef.current === nodeIdStr) {
          return;
        }
        const x = node.x * width;
        const y = node.y * height;
        const radius = node.size * (2.2 + index * 0.25);
        const pulse = 0.65 + Math.sin(time * 0.0004 + index) * 0.15;
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
        gradient.addColorStop(0, `rgba(255, 230, 200, ${0.18 * pulse})`);
        gradient.addColorStop(0.55, `rgba(255, 220, 200, ${0.06 * pulse})`);
        gradient.addColorStop(1, 'rgba(255, 220, 200, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.restore();

      const sphereMap = new Map(
        spheres.map((node) => [
          node.id,
          {
            ...node,
            x: (node.x - 0.5) * zoomScale + 0.5,
            y: (node.y - 0.5) * zoomScale + 0.5,
          },
        ])
      );
      const visibleSet = new Set(visibleSpheres.map((node) => node.id));

      // Filaments
      const activeLinks = new Set<string>();
      if (hoveredId) {
        const origin = sphereMap.get(hoveredId);
        const neighbors = adjacency.get(hoveredId) || [];
        if (origin) {
          const sorted = neighbors
            .map((id) => {
              const target = sphereMap.get(id);
              if (!target) return null;
              const dx = origin.x - target.x;
              const dy = origin.y - target.y;
              return { id, dist: Math.hypot(dx, dy) };
            })
            .filter((entry): entry is { id: string; dist: number } => entry !== null)
            .sort((a, b) => a.dist - b.dist)
            .slice(0, preset.hoverLinks);
          sorted.forEach((entry) => {
            const key = `${hoveredId}-${entry.id}`;
            activeLinks.add(key);
            linkActivityRef.current.set(key, time);
          });
        }
      }

      const renderIdleFilaments = preset.baseFilamentAlpha > 0.02;
      const edgeStep = hoveredId ? 1 : linkParams.edgeStepIdle;

      if (hoveredId || renderIdleFilaments) {
        ctx.save();
        ctx.translate(sphereOffset.x, sphereOffset.y);
        ctx.translate(width * 0.5, height * 0.5);
        ctx.scale(zoomScale, zoomScale);
        ctx.translate(-width * 0.5, -height * 0.5);
        ctx.globalCompositeOperation = 'screen';
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        edges.forEach((edge, index) => {
          if (!hoveredId && index % edgeStep !== 0) return;
        const from = sphereMap.get(edge.from);
        const to = sphereMap.get(edge.to);
        if (!from || !to) return;
        if (!visibleSet.has(edge.from) || !visibleSet.has(edge.to)) return;
        // Скрываем связи, которые идут к сфере, являющейся целью анимации приближения
        const hiddenId = hiddenSphereIdRef.current || (enteringSphere ? String(enteringSphere.id) : null);
        if (hiddenId) {
          if (String(edge.from) === hiddenId || String(edge.to) === hiddenId) {
            return;
          }
        }

        const idKey = `${edge.from}-${edge.to}`;
          const isActive = activeLinks.has(idKey);
          const lastActive = linkActivityRef.current.get(idKey);
          if (isActive) {
            if (!linkAssembleRef.current.has(idKey)) {
              linkAssembleRef.current.set(idKey, time);
            }
          } else if (!lastActive) {
            linkAssembleRef.current.delete(idKey);
          }
          const baseAlpha = renderIdleFilaments ? linkParams.idleAlpha : 0.0;
          let alpha = isActive ? linkParams.hoverAlpha : baseAlpha;
          if (!isActive && lastActive) {
            const ageSec = Math.max(0, (time - lastActive) / 1000);
            if (ageSec <= linkParams.holdSeconds) {
              alpha = linkParams.hoverAlpha * 0.9;
            } else if (ageSec <= linkParams.holdSeconds + linkParams.fadeSeconds) {
              const t = (ageSec - linkParams.holdSeconds) / linkParams.fadeSeconds;
              alpha = linkParams.hoverAlpha * (1 - t);
            } else {
              alpha = baseAlpha;
            }
          }
          alpha *= 0.25 + 0.75 * filamentFade;
          if (!isActive && alpha <= 0.005) return;

        const fromX = from.x * width;
        const fromY = from.y * height;
        const toX = to.x * width;
        const toY = to.y * height;
        const dx = toX - fromX;
        const dy = toY - fromY;
        const dist = Math.hypot(dx, dy) || 1;
        const fromRadius = Math.max(4, from.size ?? 0);
        const toRadius = Math.max(4, to.size ?? 0);
        const fromEdgeX = fromX + (dx / dist) * fromRadius;
        const fromEdgeY = fromY + (dy / dist) * fromRadius;
        const toEdgeX = toX - (dx / dist) * toRadius;
        const toEdgeY = toY - (dy / dist) * toRadius;
        const seedKey = `${edge.from}-${edge.to}-${seed}`;
        const control = edgeControlMap.get(seedKey) || { tx: 0.5, ty: 0.5, amp: 18 };
        const drift = isActive ? Math.sin(time * 0.00025 + control.tx * 8) * linkParams.driftAmp : 0;
        const ctrlX = lerp(fromEdgeX, toEdgeX, control.tx) + linkParams.curveAmp + drift;
        const ctrlY = lerp(fromEdgeY, toEdgeY, control.ty) - linkParams.curveAmp * 0.5;
        const assembleSeconds = Math.max(0.05, linkParams.assembleSeconds);
        const assembleStart = linkAssembleRef.current.get(idKey) ?? time;
        const assembleProgress =
          linkParams.assembleEnabled && isActive
            ? Math.min(1, Math.max(0.02, (time - assembleStart) / 1000 / assembleSeconds))
            : 1;

        const buildQuadraticPath = (
          startX: number,
          startY: number,
          controlX: number,
          controlY: number,
          endX: number,
          endY: number,
          tStart: number,
          tEnd: number
        ) => {
          const clampedStart = Math.max(0, Math.min(1, tStart));
          const clampedEnd = Math.max(clampedStart, Math.min(1, tEnd));
          if (clampedEnd <= clampedStart + 0.0001) return;
          if (clampedStart <= 0.001 && clampedEnd >= 0.999) {
            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.quadraticCurveTo(controlX, controlY, endX, endY);
            return;
          }
          const span = clampedEnd - clampedStart;
          const steps = Math.max(12, Math.round(60 * span));
          ctx.beginPath();
          for (let i = 0; i <= steps; i += 1) {
            const t = clampedStart + (i / steps) * span;
            const mt = 1 - t;
            const x = mt * mt * startX + 2 * mt * t * controlX + t * t * endX;
            const y = mt * mt * startY + 2 * mt * t * controlY + t * t * endY;
            if (i === 0) {
              ctx.moveTo(x, y);
            } else {
              ctx.lineTo(x, y);
            }
          }
        };

        const quadPoint = (t: number) => {
          const mt = 1 - t;
          return {
            x: mt * mt * fromEdgeX + 2 * mt * t * ctrlX + t * t * toEdgeX,
            y: mt * mt * fromEdgeY + 2 * mt * t * ctrlY + t * t * toEdgeY,
          };
        };

        const quadTangent = (t: number) => {
          const dxT = 2 * (1 - t) * (ctrlX - fromEdgeX) + 2 * t * (toEdgeX - ctrlX);
          const dyT = 2 * (1 - t) * (ctrlY - fromEdgeY) + 2 * t * (toEdgeY - ctrlY);
          const len = Math.hypot(dxT, dyT) || 1;
          return { x: dxT / len, y: dyT / len };
        };

          const endT = assembleProgress;
          const baseGlowWidth = isActive ? 7.0 : 3.8;
          const baseLineWidth = isActive ? 1.6 : 1.0;
          const fogWidth = baseGlowWidth * 6.2;

          const drawFogFunnel = (progress: number, strength: number) => {
            const clamped = Math.max(0.02, Math.min(1, progress));
            const falloff = Math.max(0.08, linkParams.fogCurvature);
            const targetDistance = Math.max(3, fromRadius * 1.2);
            const maxDistance = Math.max(0, dist - toRadius);
            const goal = Math.min(targetDistance, maxDistance);
            let low = 0.02;
            let high = 1.0;
            for (let i = 0; i < 8; i += 1) {
              const mid = (low + high) / 2;
              const point = quadPoint(mid);
              const d = Math.hypot(point.x - fromEdgeX, point.y - fromEdgeY);
              if (d < goal) {
                low = mid;
              } else {
                high = mid;
              }
            }
            const bendT = Math.max(0.02, Math.min(0.9, low));
            const fogT = Math.max(0.02, bendT * clamped);
            const fogPoint = quadPoint(fogT);
            const baseDir = quadTangent(Math.min(0.12, fogT));
            const tipDir = quadTangent(fogT);
            const intensity = Math.min(0.5, strength * (0.35 + clamped * 0.65));
            const gradient = ctx.createLinearGradient(fromEdgeX, fromEdgeY, fogPoint.x, fogPoint.y);
            gradient.addColorStop(0, `rgba(255, 240, 210, ${intensity * 0.9})`);
            gradient.addColorStop(0.5, `rgba(255, 235, 200, ${intensity * 0.5})`);
            gradient.addColorStop(0.85, `rgba(255, 235, 200, ${intensity * 0.2})`);
            gradient.addColorStop(1, 'rgba(255, 235, 200, 0)');
            ctx.save();
            ctx.globalCompositeOperation = 'source-over';
            ctx.fillStyle = gradient;
            ctx.shadowBlur = fogWidth * 0.2;
            ctx.shadowColor = `rgba(255, 235, 200, ${Math.min(0.35, intensity * 0.8)})`;
            const baseScale = linkParams.fogBaseScale;
            const baseAngle = Math.atan2(baseDir.y, baseDir.x);
            const arcRadius = Math.max(8, fromRadius);
            const baseWidth = fogWidth * baseScale;
            const baseHalfAngle = Math.min(Math.PI / 2.2, (baseWidth / arcRadius) * 0.35);
            const neckWidth = fogWidth * 0.08;
            const tipLeftX = fogPoint.x + (-tipDir.y) * neckWidth;
            const tipLeftY = fogPoint.y + tipDir.x * neckWidth;
            const tipRightX = fogPoint.x + tipDir.y * neckWidth;
            const tipRightY = fogPoint.y + (-tipDir.x) * neckWidth;
            const steps = 10;
            ctx.beginPath();
            ctx.arc(fromX, fromY, arcRadius, baseAngle - baseHalfAngle, baseAngle + baseHalfAngle);
            for (let i = steps; i >= 0; i -= 1) {
              const t = i / steps;
              const span = baseHalfAngle * 2;
              const angle = baseAngle + baseHalfAngle - span * t;
              const widthScale = 0.12 + (baseScale - 0.12) * Math.exp(-t / falloff);
              const neckOffset = neckWidth * (0.2 + widthScale);
              const sideX = fromX + Math.cos(angle) * arcRadius + (-Math.sin(angle) * neckOffset);
              const sideY = fromY + Math.sin(angle) * arcRadius + (Math.cos(angle) * neckOffset);
              if (i === steps) {
                ctx.lineTo(tipLeftX, tipLeftY);
              }
              ctx.lineTo(sideX, sideY);
            }
            ctx.lineTo(tipRightX, tipRightY);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          };

          // мягкое свечение под нитями
          ctx.strokeStyle = `rgba(255, 230, 190, ${alpha * (isActive ? 0.55 : 0.2)})`;
          const fogProgress = linkParams.assembleEnabled && isActive ? assembleProgress : 1;
          const fogStrength = alpha * (isActive ? 0.9 : 0.4);
          drawFogFunnel(fogProgress, fogStrength);
          ctx.lineWidth = baseGlowWidth;
          buildQuadraticPath(fromEdgeX, fromEdgeY, ctrlX, ctrlY, toEdgeX, toEdgeY, 0, endT);
          ctx.stroke();

          // основная тонкая нить
          ctx.strokeStyle = `rgba(255, 230, 190, ${alpha})`;
          ctx.lineWidth = baseLineWidth;
          buildQuadraticPath(fromEdgeX, fromEdgeY, ctrlX, ctrlY, toEdgeX, toEdgeY, 0, endT);
          ctx.stroke();
        });
        ctx.restore();
      }

      // Spheres
      ctx.save();
      ctx.translate(sphereOffset.x, sphereOffset.y);
      ctx.translate(width * 0.5, height * 0.5);
      ctx.scale(zoomScale, zoomScale);
      ctx.translate(-width * 0.5, -height * 0.5);
      visibleSpheres.forEach((node) => {
        // Скрываем сферу, если она является целью анимации приближения
        const nodeIdStr = String(node.id);
        if ((enteringSphere && String(enteringSphere.id) === nodeIdStr) || hiddenSphereIdRef.current === nodeIdStr) {
          return;
        }
        const zx = (node.x - 0.5) * zoomScale + 0.5;
        const zy = (node.y - 0.5) * zoomScale + 0.5;
        const x = zx * width;
        const y = zy * height;
        const radius = node.size;
        const isHover = hoveredId === node.id;
        const revealAt = revealTimes.get(node.id) ?? performance.now();
        const revealProgress = Math.min((time - revealAt) / 800, 1);
        const appearAlpha = 0.15 + revealProgress * 0.85;

        const sprite = sphereVariant === 'sprite-full' ? spriteFullRef.current : sphereVariant === 'sprite-ring' ? spriteRingRef.current : null;

        if (sprite) {
          const offset = sphereOffsets.get(node.id) || { x: 0, y: 0 };
          ctx.save();
          ctx.globalAlpha = (isHover ? 0.8 : 0.25) * appearAlpha;
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(
            sprite,
            x - radius + offset.x * radius,
            y - radius + offset.y * radius,
            radius * 2,
            radius * 2
          );
          ctx.restore();
        } else {
          const gradient = ctx.createRadialGradient(x, y, radius * 0.12, x, y, radius);
          gradient.addColorStop(0, 'rgba(252, 255, 161, 0)');
          gradient.addColorStop(0.6, 'rgba(219, 253, 250, 0.06)');
          gradient.addColorStop(1, 'rgba(255,255,255,0.02)');

          // Внутреннее неоднородное темное свечение (короткое, 2-3px)
          const innerShadow = ctx.createRadialGradient(x, y, radius * 0.55, x, y, radius);
          innerShadow.addColorStop(0, 'rgba(252, 255, 161, 0)');
          innerShadow.addColorStop(0.95, 'rgba(132, 154, 192, 0.29)');
          innerShadow.addColorStop(0.99, 'rgba(255, 254, 218, 0.3)');

          // Наружное светло-голубое свечение (увеличенное, заметное)
          ctx.save();
          ctx.shadowBlur = isHover ? 48 : 36;
          ctx.shadowColor = isHover ? 'rgba(185, 220, 255, 0.45)' : 'rgba(160, 205, 255, 0.32)';
          ctx.globalAlpha = (isHover ? 0.85 : 0.65) * appearAlpha;
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();

          // Дополнительный внешний ореол (тонкая дымка снаружи)
          ctx.save();
          const outerGlow = ctx.createRadialGradient(x, y, radius * 1.2, x, y, radius * 1.8);
          outerGlow.addColorStop(0, 'rgba(160, 205, 255, 0.18)');
          outerGlow.addColorStop(1, 'rgba(160, 205, 255, 0.0)');
          ctx.fillStyle = outerGlow;
          ctx.beginPath();
          ctx.arc(x, y, radius * 2.8, 0, Math.PI * 4);
          ctx.fill();
          ctx.restore();

          // Внутренняя темная кайма
          ctx.save();
          ctx.globalAlpha = 0.7 * appearAlpha;
          ctx.fillStyle = innerShadow;
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();

          ctx.strokeStyle = isHover ? 'rgba(200,230,255,0.6)' : 'rgba(170,210,255,0.35)';
          ctx.lineWidth = isHover ? 1.5 : 1.1;
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.stroke();

          const texture = textureRef.current;
          if (texture) {
            const offset = sphereOffsets.get(node.id) || { x: 0, y: 0 };
            ctx.save();
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.clip();
            ctx.globalAlpha = 0.06 * appearAlpha;
            ctx.drawImage(
              texture,
              x - radius + offset.x * radius,
              y - radius + offset.y * radius,
              radius * 2,
              radius * 2
            );
            ctx.restore();
          }
        }
      });
      ctx.restore();

      // Лёгкий туман поверх (слегка приглушает фон)
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const fog = ctx.createRadialGradient(width * 0.5, height * 0.4, 0, width * 0.5, height * 0.4, Math.max(width, height) * 0.55);
      fog.addColorStop(0, 'rgba(255, 245, 235, 0.05)');
      fog.addColorStop(1, 'rgba(255, 245, 235, 0)');
      ctx.fillStyle = fog;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();

      // Reveal mask
      const revealElapsed = (time - revealStartRef.current) / 1000;
      const revealProgress = Math.min(revealElapsed / 1.0, 1);
      if (revealProgress < 1) {
        const centerX = (focus.x / 100) * width;
        const centerY = (1 - focus.y / 100) * height;
        const maxRadius = Math.hypot(width, height) * 0.5;
        const radius = maxRadius * (0.2 + 0.8 * revealProgress);
        const gradient = ctx.createRadialGradient(centerX, centerY, radius * 0.2, centerX, centerY, radius);
        gradient.addColorStop(0, 'rgba(255,255,255,1)');
        gradient.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.globalCompositeOperation = 'destination-in';
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'source-over';
      }

      // Metrics
      frameCountRef.current += 1;
      if (lastFpsTimeRef.current === 0) lastFpsTimeRef.current = time;
      const elapsed = time - lastFpsTimeRef.current;
      if (elapsed >= 500) {
        const fps = (frameCountRef.current * 1000) / elapsed;
        setMetrics({ fps, frameMs, draws: edges.length });
        frameCountRef.current = 0;
        lastFpsTimeRef.current = time;
      }

      animationRef.current = requestAnimationFrame(render);
    };

    animationRef.current = requestAnimationFrame(render);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [adjacency, dustPoints, edges, focus.x, focus.y, hoveredId, preset, spheres, visibleSpheres]);

  const handleMouseMove = (e: ReactMouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    mouseRef.current = { x, y };
    setParallax({ x: (x - 0.5) * 20, y: (y - 0.5) * 20 });
    lastInteractionRef.current = Date.now();

    // Hover detection
      const width = rect.width * Math.min(window.devicePixelRatio || 1, preset.dprCap) * preset.renderScale;
      const height = rect.height * Math.min(window.devicePixelRatio || 1, preset.dprCap) * preset.renderScale;
      const px = x * width;
      const py = y * height;

    if (!dragRef.current) {
      let nextHovered: string | null = null;
      visibleSpheres.forEach((node) => {
        const zx = (node.x - 0.5) * zoom + 0.5;
        const zy = (node.y - 0.5) * zoom + 0.5;
        const sx = zx * width;
        const sy = zy * height;
        const dist = Math.hypot(px - sx, py - sy);
        if (dist <= node.size * 1.05) {
          nextHovered = node.id;
        }
      });
      setHoveredId(nextHovered);
    } else {
      setHoveredId(dragRef.current.id);
    }

    if (dragRef.current) {
      const dragId = dragRef.current.id;
      const dx = x - dragRef.current.startX;
      const dy = y - dragRef.current.startY;
      if (Math.hypot(dx, dy) > 0.006) {
        setIsDragging(true);
        setMapData((prev) => ({
          ...prev,
          nodes: prev.nodes.map((node) => {
            if (String(node.id) !== String(dragId)) return node;
            const newX = Math.min(0.98, Math.max(0.02, node.x + dx / zoom));
            const newY = Math.min(0.98, Math.max(0.02, node.y + dy / zoom));
            dragRef.current = {
              id: dragId,
              startX: x,
              startY: y,
            };
            return { ...node, x: newX, y: newY };
          }),
        }));
      }
    }
  };

  const handlePointerDown = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!hoveredId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    dragRef.current = {
      id: hoveredId,
      startX: x,
      startY: y,
    };
    setIsDragging(false);
  };

  const handlePointerUp = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    // Если было перетаскивание, не запускаем переход
    if (isDragging) return;
    const targetId = hoveredId || drag.id;
    if (!targetId) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const node = spheres.find((item) => item.id === targetId);
      if (node) {
        const zoomScale = zoom;
        const mx = mouseRef.current.x - 0.5;
        const my = mouseRef.current.y - 0.5;
        const sphereOffset = { x: mx * 12, y: my * 12 };
        const zx = (node.x - 0.5) * zoomScale + 0.5;
        const zy = (node.y - 0.5) * zoomScale + 0.5;
        const cx = zx * rect.width + sphereOffset.x;
        const cy = zy * rect.height + sphereOffset.y;
        const radius = node.size;
        const maxDist = Math.max(
          Math.hypot(cx, cy),
          Math.hypot(rect.width - cx, cy),
          Math.hypot(cx, rect.height - cy),
          Math.hypot(rect.width - cx, rect.height - cy)
        );
        const scale = Math.max(3, maxDist / Math.max(8, radius)) * 1.05;
        const sphereId = String(targetId);
        // Скрываем сферу только при клике (не при перетаскивании)
        hiddenSphereIdRef.current = sphereId;
        setEnteringSphere({ id: sphereId, x: cx, y: cy, radius, scale });
      }
    }
    const customEvent = new CustomEvent('membrane-sphere-enter', {
      detail: { sphereId: targetId },
    });
    window.dispatchEvent(customEvent);
    if (onProceed) {
      if (enterTimerRef.current) {
        window.clearTimeout(enterTimerRef.current);
      }
      enterTimerRef.current = window.setTimeout(() => {
        setEnteringSphere(null);
        hiddenSphereIdRef.current = null;
        onProceed();
      }, 720);
    }
  };

  const handleWheel = (e: ReactWheelEvent<HTMLDivElement>) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    const delta = e.deltaY > 0 ? -0.02 : 0.02;
    setZoom((prev) => Math.min(1.2, Math.max(0.85, prev + delta)));
    lastInteractionRef.current = Date.now();
  };

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: 'easeInOut' }}
      className="absolute inset-0 w-full h-full overflow-hidden"
      onMouseMove={handleMouseMove}
      onMouseDown={handlePointerDown}
      onMouseUp={handlePointerUp}
      onWheel={handleWheel}
    >
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          backgroundImage:
            `${backgroundImage}, radial-gradient(circle at 40% 30%, rgba(255,220,200,0.25), transparent 55%), radial-gradient(circle at 70% 70%, rgba(160,180,255,0.2), transparent 60%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'saturate(1.1)',
          transform: `translate(${staticBg ? 0 : parallax.x}px, ${staticBg ? 0 : parallax.y}px)`,
        }}
      />
      <div
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{
          backgroundImage: lightCoreGradients,
          opacity: 0.6,
          mixBlendMode: 'screen',
          transform: `translate(${staticBg ? 0 : parallax.x * 0.6}px, ${staticBg ? 0 : parallax.y * 0.6}px)`,
        }}
      />
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          backgroundImage:
            `${backgroundImage}, radial-gradient(circle at 60% 20%, rgba(255,255,255,0.18), transparent 45%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          mixBlendMode: 'screen',
          opacity: 0.3,
          transform: `translate(${staticBg ? 0 : parallax.x * 0.5}px, ${staticBg ? 0 : parallax.y * 0.5}px)`,
        }}
      />

      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      {enteringSphere ? (
        <motion.div
          className="absolute inset-0 z-30 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            initial={{ scale: 1, opacity: 0.35 }}
            animate={{ scale: enteringSphere.scale, opacity: 1 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              left: enteringSphere.x - enteringSphere.radius,
              top: enteringSphere.y - enteringSphere.radius,
              width: enteringSphere.radius * 2,
              height: enteringSphere.radius * 2,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(255,255,255,0.02) 0%, rgba(210,225,255,0.18) 45%, rgba(160,200,255,0.35) 70%, rgba(255,255,255,0.95) 100%)',
              boxShadow: '0 0 40px rgba(180,210,255,0.45), 0 0 120px rgba(255,255,255,0.35)',
              mixBlendMode: 'screen',
            }}
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.55 }}
            transition={{ duration: 0.72, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(circle at center, rgba(255,255,255,0.0) 0%, rgba(240,245,255,0.12) 40%, rgba(250,250,255,0.7) 100%)',
            }}
          />
        </motion.div>
      ) : null}

      {import.meta.env.DEV && (
      <div
        className="absolute top-4 right-4 text-white/90 text-xs z-20"
        style={{
          background: 'rgba(10, 15, 30, 0.55)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '10px',
          padding: '12px 12px 10px',
          backdropFilter: 'blur(6px)',
          width: '220px',
        }}
      >
        <div className="text-white/80 mb-2" style={{ letterSpacing: '0.4px' }}>
          Reveal Map
        </div>
        <div className="flex gap-1 mb-2">
          {(['low', 'medium', 'high'] as const).map((presetId) => (
            <button
              key={presetId}
              type="button"
              onClick={() => setQuality(presetId)}
              className={`px-2 py-1 rounded border text-[10px] ${
                quality === presetId
                  ? 'border-white/40 text-white/90'
                  : 'border-white/20 text-white/60'
              }`}
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              {presetId.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="flex gap-1 mb-2">
          {(['reference', 'clean'] as const).map((variant) => (
            <button
              key={variant}
              type="button"
              onClick={() => setBgVariant(variant)}
              className={`px-2 py-1 rounded border text-[10px] ${
                bgVariant === variant ? 'border-white/40 text-white/90' : 'border-white/20 text-white/60'
              }`}
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              {variant === 'reference' ? 'BG • Библиотека' : 'BG • Свои'}
            </button>
          ))}
        </div>
        <div className="flex gap-1 mb-2">
          {(['procedural', 'sprite-full', 'sprite-ring'] as const).map((variant) => (
            <button
              key={variant}
              type="button"
              onClick={() => setSphereVariant(variant)}
              className={`px-2 py-1 rounded border text-[10px] ${
                sphereVariant === variant ? 'border-white/40 text-white/90' : 'border-white/20 text-white/60'
              }`}
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              {variant === 'procedural'
                ? 'Sphere • Procedural'
                : variant === 'sprite-full'
                ? 'Sphere • Full'
                : 'Sphere • Ring'}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 mb-2 text-white/70">
          <input
            type="checkbox"
            checked={staticBg}
            onChange={(e) => setStaticBg(e.target.checked)}
          />
          <span>Статичный фон</span>
        </label>
        <div className="text-white/70 mb-2">Связи</div>
        <label className="flex items-center gap-2 mb-2 text-white/70">
          <input
            type="checkbox"
            checked={linkParams.assembleEnabled}
            onChange={(e) =>
              setLinkParams((prev) => ({
                ...prev,
                assembleEnabled: e.target.checked,
              }))
            }
          />
          <span>Сборка нитей</span>
        </label>
        {linkSliderItems.map((item) => (
          <label key={item.key} className="block mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-white/70">{item.label}</span>
              <span className="text-white/50">
                {item.isInt
                  ? Math.round(linkParams[item.key] as number)
                  : Number(linkParams[item.key]).toFixed(2)}
              </span>
            </div>
            <input
              type="range"
              min={item.min}
              max={item.max}
              step={item.step}
              value={linkParams[item.key] as number}
              onChange={(e) =>
                setLinkParams((prev) => ({
                  ...prev,
                  [item.key]: parseFloat(e.target.value),
                }))
              }
              className="w-full accent-blue-200"
            />
          </label>
        ))}
        {metrics ? (
          <div className="text-white/60">
            FPS: {metrics.fps.toFixed(1)} · {metrics.frameMs.toFixed(1)} ms
          </div>
        ) : (
          <div className="text-white/60">Метрики: ожидание…</div>
        )}
        {loading && <div className="text-white/50 mt-2">Кристаллизация…</div>}
        {error && <div className="text-white/50 mt-2">⚠ {error}</div>}
      </div>
      )}
    </motion.div>
  );
}
